// The decline-time gate for Autofake, and the only piece of it the bulk download
// module talks to. Statically importable on purpose: everything here is cheap and
// synchronous, and the heavy modal chunk (React components, product bodies) is
// dynamically imported only after a decline actually wins the roll — so the ~90%
// of declines that lose resume the user's download synchronously, inside the
// dismiss click's user activation, exactly as they did before Autofake existed.
import { markModalOpening } from 'js/utils/modalPresence';
import {
  BulkDownloadableWork,
  BulkDownloadTrigger,
  giveGatedDownloadOnUpsellClose,
  navigateToCheckout,
} from 'js/bulk_download/bulk_download_helper';
import { fetchBulkDownloadCountsCached } from 'js/bulk_download/bulk_download_counts_cache';
import type { BundleWork } from 'js/bulk_download/work_cards_shared/types';
import type { AutofakeFlags } from 'js/auto_props/types/autofake_flags';

import { loadAutofakeFlow } from './loadAutofakeFlow';
import { getSeenIdeas } from './seenProducts';
import type { AutofakeTreatment } from './types';

// The price the confirmed modal offers the PDF Pack at: the same localized
// Pricing::TrialHelper value the upsell modal the user just declined shows
// (BulkDownloadFlags.trialPrice). Read quietly rather than via
// bulkDownloadTrialPrice() in show_bulk_download_modal.tsx, which would be an
// import cycle — that file imports this one. Absent (no low-cost-trial plan for
// the user's country) makes the confirmed modal drop its checkout line rather
// than render a gap.
function quietTrialPrice(): string | undefined {
  try {
    const map = (
      window as {
        __autoProps?: { BulkDownloadFlags?: { trialPrice?: string } };
      }
    ).__autoProps;
    return map?.BulkDownloadFlags?.trialPrice || undefined;
  } catch {
    return undefined;
  }
}

// If the modal chunk hasn't arrived by now, give the user their download and skip
// Autofake for this decline. Without a deadline, a request that never settles (as
// opposed to rejecting) would strand the promised PDF forever. Bounded by the
// browser's TRANSIENT USER ACTIVATION window (~5s in Chrome): the resume clicks a
// target="_blank" anchor, which is popup-blocked once the dismiss click's
// activation expires, so a later deadline would drop the PDF it exists to save.
const CHUNK_TIMEOUT_MS = 3000;

export interface StartOptions {
  trigger: BulkDownloadTrigger;
  work: BulkDownloadableWork;
  /** The caller gated a single-PDF download behind the upsell. */
  gatesADownload: boolean;
  /** The pack the upsell modal showed, captured by the caller. */
  packWorks: BundleWork[];
  /** Resume the bulk download chain — starts the PDF the user was promised. */
  onContinue: () => void;
}

/**
 * Reads AutofakeFlags without going through getAutoProps, which console.errors
 * when the key is absent — and absent is the NORMAL state here: the flag off, or
 * any surface whose controller doesn't register the prop. Same tolerate-absence
 * treatment as bulk_download/autotest/publishedArm.ts gives its accessor.
 */
export function quietAutofakeFlags(): AutofakeFlags | undefined {
  try {
    const map = (window as { __autoProps?: { AutofakeFlags?: AutofakeFlags } })
      .__autoProps;
    return map?.AutofakeFlags;
  } catch {
    return undefined;
  }
}

/**
 * Pick a live treatment to show. Normally a browser never sees the same idea twice
 * and works through the catalogue in a random order.
 *
 * Under the testing flag the seen-list is IGNORED, so QA can see every product
 * repeatedly without clearing localStorage between runs. Tied to that flag rather
 * than a constant so there is nothing to remember to switch back. The list is
 * still WRITTEN either way — only the read is skipped — so a browser used for QA
 * behaves correctly the moment the flag comes off.
 */
function pickTreatment(
  rotation: AutofakeTreatment[],
  ignoreSeenList: boolean,
): AutofakeTreatment | null {
  const seen = new Set(ignoreSeenList ? [] : getSeenIdeas());
  const unseen = rotation.filter((t) => !seen.has(t.ideaKey));
  if (unseen.length === 0) return null;
  return unseen[Math.floor(Math.random() * unseen.length)];
}

/**
 * Roll for this decline. Independent per decline rather than a fixed cohort, so
 * exposure spreads thinly across all users instead of concentrating on a few.
 *
 * Rolled AFTER the seen-list check, so a user with nothing left to show does not
 * consume a win on an empty catalogue — that would silently depress the observed
 * rate below the configured one.
 */
function winsRoll(ratePercent: number): boolean {
  return Math.random() * 100 < ratePercent;
}

/**
 * Show the Autofake product modal in place of doing nothing after a bulk download
 * decline. Returns false when Autofake does not apply — flag off, nothing in
 * rotation, pack not loaded, every idea already seen, or the roll lost — in which
 * case the caller resumes its normal chain untouched and synchronously.
 *
 * When it returns true it OWNS the resume: `onContinue` fires exactly once, from
 * whichever comes first of a modal exit, a chunk failure, or the timeout. The
 * user was already promised that download and it must never be silently dropped
 * — nor started twice.
 */
export function maybeStartAutofake(options: StartOptions): boolean {
  const flags = quietAutofakeFlags();
  if (!flags?.enabled || !flags.rotation?.length) return false;

  // No papers, no Autofake. The blue boxes are built around the real pack, so
  // enforcing it at this boundary is what lets every one of them skip a no-data
  // rendering. Empty means the user declined before the works fetch resolved,
  // or it failed: rare, and it costs one impression.
  if (options.packWorks.length === 0) return false;

  const treatment = pickTreatment(flags.rotation, flags.testingFlag);
  if (!treatment) return false;
  if (!winsRoll(flags.ratePercent)) return false;

  const { trigger, work, gatesADownload, packWorks, onContinue } = options;

  // Mirrors the conjunction maybeDownloadWork checks before starting the gated
  // PDF; drives the modal's dismiss copy so it never promises a download that
  // isn't coming (the PDF Package buttons gate nothing on decline).
  const hasPendingDownload =
    gatesADownload &&
    giveGatedDownloadOnUpsellClose(trigger) &&
    !!work.attachment.url;

  // Exactly-once resume, however many of the exits below race: a modal close, a
  // chunk failure, and the timeout can each try to hand the chain back.
  let continued = false;
  const continueOnce = () => {
    if (continued) return;
    continued = true;
    onContinue();
  };

  // Marked before the dynamic import: without this, a deferred launcher (the
  // full screen premium modal) running in the microtask after the upsell close
  // would see no modal present and stack on top of Autofake as it mounts.
  // Released once the flow has marked its own presence, or on any no-show path.
  const releaseBridge = markModalOpening();

  let timedOut = false;
  const deadline = window.setTimeout(() => {
    timedOut = true;
    releaseBridge();
    continueOnce();
  }, CHUNK_TIMEOUT_MS);

  // The counts the product copy interpolates. Normally already cached by the
  // LISWP surfaces; on canonical-card surfaces this is a fresh request, which is
  // why it only happens on winning rolls. A failure just means the copy renders
  // zeroes rather than blocking the download.
  Promise.all([
    loadAutofakeFlow(),
    fetchBulkDownloadCountsCached(Number(work.id)).catch(() => null),
  ])
    .then(([{ showAutofakeFlow }, counts]) => {
      window.clearTimeout(deadline);
      // Past the deadline the download already resumed; popping the modal on
      // top of it now would read as a malfunction. Skip this impression.
      if (timedOut) return;

      showAutofakeFlow({
        treatment,
        trigger,
        sourceWorkId: Number(work.id) || null,
        context: {
          workTitle: work.title,
          authorName: work.authors[0]?.displayName,
          citationCount: counts?.filter_counts?.citingThis,
          relatedCount: counts?.filter_counts?.topRelated,
          works: packWorks,
        },
        trialPrice: quietTrialPrice(),
        testingFlag: flags.testingFlag,
        hasPendingDownload,
        onContinue: continueOnce,
        onCheckout: () =>
          navigateToCheckout('autofake-confirmed', work.id, new Set()),
      });
      releaseBridge();
    })
    .catch(() => {
      window.clearTimeout(deadline);
      releaseBridge();
      continueOnce();
    });

  return true;
}
